<template>
  <ProgressStepsItem
    :state="state === STATES.LOADING ? 'in_progress' : 'todo'"
    data-qa="download-kaia-links"
  >
    <BaseBanner
      v-if="state === STATES.LOADING"
      variant="neutral"
      title="&nbsp;"
      description="&nbsp;"
    />
    <BaseBanner
      v-if="state === STATES.DEEPLINK_TO_APP"
      scroll-into-view
      variant="neutral"
      :title="$t('us__download_page__info_header__deeplink__with_user')"
      :description="
        $t('us__download_page__info_description__deeplink__with_user')
      "
    >
      <template #actions>
        <BaseButton
          class="cta"
          size="small"
          :text="$t('b2b_get_started_btn_text')"
          @click="handleRedirectLinkClickEvent"
        />
      </template>
    </BaseBanner>
    <BaseBanner
      v-if="state === STATES.MOBILE_DOWNLOAD"
      scroll-into-view
      variant="neutral"
      :title="$t('download_page__ctrls_header')"
      :description="$t('download_page__ctrls_description')"
    >
      <template #actions>
        <StoreBadge
          v-if="likelyHasAppStore()"
          :lang="lang"
          app-type="ios"
          @click="handleStoreLinkClick('App Store')"
        />
        <StoreBadge
          v-if="likelyHasPlayStore()"
          :lang="lang"
          app-type="android"
          @click="handleStoreLinkClick('Google Play')"
        />
      </template>
    </BaseBanner>
    <BaseBanner
      v-if="state === STATES.DESKTOP_DOWNLOAD"
      scroll-into-view
      variant="neutral"
      :title="$t('download_page__ctrls_header')"
      :description="$t('download_page__ctrls_description')"
    >
      <template #actions>
        <div class="store-grid">
          <StoreBadge
            class="store-grid__item"
            :lang="lang"
            app-type="ios"
            @click="handleStoreLinkClick('App Store')"
          />
          <img
            class="store-grid__item"
            :src="appleQRCodeUrl"
            alt="Kaia iOS QR Code"
          />
          <StoreBadge
            class="store-grid__item"
            :lang="lang"
            app-type="android"
            @click="handleStoreLinkClick('Google Play')"
          />
          <img
            class="store-grid__item"
            :src="googleQRCodeUrl"
            alt="Kaia Android QR Code"
          />
        </div>
      </template>
    </BaseBanner>
  </ProgressStepsItem>
</template>
<script setup>
import BaseBanner from '@shared/components/BaseBanner.vue'
import ProgressStepsItem from '@shared/components/ProgressStepsItem.vue'
import { isFromApp } from '@shared/helpers/isFromApp.js'
import { likelyHasAppStore, likelyHasPlayStore, wait } from '@shared/utils.js'
import { computed, onMounted, ref } from 'vue'
import getQRCodeUrl from '@shared/helpers/getQRCodeUrl.js'
import useUserStore from '@shared/stores/useUserStore.js'
import useCorporateStore from '@shared/stores/useCorporateStore.js'
import assembleKaiaAppDeepLink from '@shared/helpers/assembleKaiaAppDeepLink.js'
import Tracker from '@shared/Tracker.js'
import StoreBadge from '@shared/components/StoreBadge.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import useSharedStore from '@shared/stores/useSharedStore.js'

const userStore = useUserStore()
const corporateStore = useCorporateStore()
const sharedStore = useSharedStore()

const STATES = {
  LOADING: 0,
  DEEPLINK_TO_APP: 1,
  MOBILE_DOWNLOAD: 2,
  DESKTOP_DOWNLOAD: 3,
}

const showSpinner = ref(false)
const user = computed(() => userStore.user)
const lang = computed(() => userStore.lang)
const corporate = computed(() => corporateStore.corporate)
const redirectLink = computed(() => sharedStore.redirectLink)

const appleQRCodeUrl = computed(() => getQRCodeUrl(lang.value, 'ios'))
const googleQRCodeUrl = computed(() => getQRCodeUrl(lang.value, 'android'))

const state = computed(() => {
  switch (true) {
    case showSpinner.value:
      return STATES.LOADING
    case isFromApp():
      return STATES.DEEPLINK_TO_APP
    case likelyHasAppStore() || likelyHasPlayStore():
      return STATES.MOBILE_DOWNLOAD
    default:
      return STATES.DESKTOP_DOWNLOAD
  }
})

const ensureDeepLink = async () => {
  // don't reload the deeplink if it already exists and was created less than 30 seconds ago
  if (redirectLink.value?.creationDate > Date.now() - 1000 * 30) {
    return
  }

  const link = await assembleKaiaAppDeepLink({
    path: 'auth/one_time_token',
    addOneTimeAuthToken: true,
    fallback: () =>
      assembleKaiaAppDeepLink({
        path: 'goto/screen',
        query: { target: 'login' },
      }),
  })
  sharedStore.redirectLink = { link, creationDate: Date.now() }
}

const handleStoreLinkClick = (storeName) => {
  Tracker.trackKaiaEvent(
    {
      event_name: 'c.lfcycl.select_button',
      app_area: 'lifecycle',
      action: 'view',
      object_type: 'screen',
      source: 'web-checkout',
      screen_name: 'signup_app_download',
    },
    {
      label: `Go to ${storeName}`,
      corporate_id: corporate.value.id,
      corporate_name: corporate.value.title,
    },
  )
}

const handleRedirectLinkClickEvent = () => {
  window.location.redirectToDeeplink(redirectLink.value.link)
}

onMounted(async () => {
  if (!user.value?.hasActiveSubscription) {
    console.warn('showing GoToAppProgressStepItem for non pro user')
  }

  if (state.value === STATES.DEEPLINK_TO_APP) {
    showSpinner.value = true
    await ensureDeepLink()
    window.location.redirectToDeeplink(redirectLink.value.link)
    await wait(1000)
    // show fallback if the deeplink does not work
    showSpinner.value = false
  }
})
</script>

<style lang="scss" scoped>
.cta {
  width: fit-content;
}

.store-grid {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: $kds-space-2x;
  margin-top: $kds-space-2x;
  justify-content: center;
  align-items: center;

  &__item {
    max-width: 160px;
    height: auto;
    margin: 0 auto;
  }

  @media (min-width: 450px) {
    display: grid;
    grid-template-columns: 1fr 1fr;

    &__item:nth-child(1) {
      order: 1;
    }
    &__item:nth-child(2) {
      order: 3;
    }
    &__item:nth-child(3) {
      order: 2;
    }
    &__item:nth-child(4) {
      order: 4;
    }
  }
}
</style>
